import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { CrisisFooter } from '@/components/CrisisFooter';
import { PHASE_WORKBOOKS, MOOD_COLORS } from '@/components/progress';
import { PhaseJourneyTimeline } from '@/components/PhaseJourneyTimeline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASES = [
  { id: 1, name: 'Stabilize' },
  { id: 2, name: 'Understand' },
  { id: 3, name: 'Reclaim' },
  { id: 4, name: 'Renew' },
];

// Section header component
const SectionHeader = ({ children }) => (
  <h2 className="text-xs tracking-[0.2em] mb-6 text-center" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
    {children}
  </h2>
);

// Animated section wrapper
const AnimatedSection = ({ delay, animateIn, children, className = '' }) => (
  <section 
    className={`mb-10 transition-all duration-700 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} ${className}`}
    style={{ transitionDelay: delay }}
  >
    {children}
  </section>
);

export const ProgressPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animateIn, setAnimateIn] = useState(false);
  const [moods, setMoods] = useState([]);

  useEffect(() => {
    const loadJourney = async () => {
      try {
        const journeyRes = await axios.get(`${API}/progress/journey`, { headers: { Authorization: `Bearer ${token}` } });
        setJourney(journeyRes.data);
        const moodHistory = journeyRes.data.mood_history || {};
        setMoods(Object.entries(moodHistory).map(([date, mood]) => ({ date, mood })));
      } catch {
        toast.error('Failed to load journey data');
      } finally {
        setLoading(false);
        setTimeout(() => setAnimateIn(true), 100);
      }
    };
    
    loadJourney();
  }, [token]);

  // Generate calendar days for mood display
  const getCalendarDays = () => {
    const days = [];
    const today = new Date();
    const moodMap = {};
    moods.forEach(m => { moodMap[m.date] = m.mood; });
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      days.push({
        date: dateStr,
        dayNum: date.getDate(),
        isToday: i === 0,
        mood: moodMap[dateStr] || null
      });
    }
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FBF9F6' }}>
        <div className="text-center">
          <div 
            className="w-16 h-16 rounded-full mx-auto mb-4 animate-pulse"
            style={{ background: 'radial-gradient(circle, rgba(201,168,76,0.4) 0%, rgba(232,180,184,0.2) 100%)' }}
          />
          <p style={{ color: '#6B6878', fontFamily: 'Jost, sans-serif' }}>
            Loading your journey...
          </p>
        </div>
      </div>
    );
  }

  const calendarDays = getCalendarDays();

  return (
    <div className="min-h-screen pb-24 relative overflow-hidden" style={{ backgroundColor: '#FBF9F6' }} data-testid="progress-page">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(201,168,76,0.04) 0%, transparent 70%)' }} />

      {/* Header */}
      <header className="p-6">
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate('/sanctuary')} className="flex items-center gap-2 mb-6 hover:opacity-70" style={{ color: '#6B6878' }}>
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm" style={{ fontFamily: 'Jost, sans-serif' }}>Back to Sanctuary</span>
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-2xl mx-auto px-6">
        {/* Title */}
        <AnimatedSection delay="0.1s" animateIn={animateIn} className="text-center">
          <h1 className="text-3xl md:text-4xl mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
            Your Healing Journey
          </h1>
          <p className="text-lg mb-4" style={{ fontFamily: 'Jost, sans-serif', color: '#4A4A5A' }}>
            {journey?.days_on_journey || 0} days of showing up for yourself
          </p>
          <span className="inline-block px-4 py-1.5 rounded-full text-sm" style={{ backgroundColor: 'rgba(201,168,76,0.15)', color: '#C9A84C', fontFamily: 'Jost, sans-serif' }}>
            Phase {journey?.current_phase}: {journey?.phase_info?.name}
          </span>
        </AnimatedSection>

        {/* Mood Calendar */}
        <AnimatedSection delay="0.2s" animateIn={animateIn}>
          <SectionHeader>EMOTIONAL WEATHER — LAST 30 DAYS</SectionHeader>
          <div className="grid grid-cols-7 gap-2">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
              <div key={`header-${i}`} className="text-center text-xs py-1" style={{ color: '#A3A0AE', fontFamily: 'Jost, sans-serif' }}>{day}</div>
            ))}
            {Array(new Date(calendarDays[0]?.date).getDay()).fill(null).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}
            {calendarDays.map((day) => (
              <div
                key={day.date}
                className={`aspect-square rounded-lg flex items-center justify-center text-xs transition-all ${day.isToday ? 'ring-2 ring-offset-2 ring-offset-[#FBF9F6]' : ''}`}
                style={{ 
                  backgroundColor: day.mood ? MOOD_COLORS[day.mood] : 'transparent',
                  border: day.mood ? 'none' : '1px solid #EAE4DC',
                  color: day.mood ? '#2E2E42' : '#A3A0AE',
                  ringColor: day.isToday ? '#C9A84C' : 'transparent'
                }}
                title={day.mood ? `${day.date}: ${day.mood}` : day.date}
              >
                {day.dayNum}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-4 mt-4 flex-wrap">
            {Object.entries(MOOD_COLORS).map(([mood, color]) => (
              <div key={mood} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
                <span className="text-xs capitalize" style={{ color: '#8A8798' }}>{mood}</span>
              </div>
            ))}
          </div>
        </AnimatedSection>

        {/* Streak */}
        <AnimatedSection delay="0.3s" animateIn={animateIn}>
          <div className="p-6 rounded-2xl text-center" style={{ background: 'rgba(201,168,76,0.06)' }}>
            <p className="text-5xl mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}>
              {journey?.current_streak || 0}
            </p>
            <p className="text-sm mb-4" style={{ fontFamily: 'Jost, sans-serif', color: '#4A4A5A' }}>day streak</p>
            <div className="flex justify-center gap-8">
              <div>
                <p className="text-lg" style={{ color: '#2E2E42', fontFamily: 'Playfair Display, serif' }}>{journey?.longest_streak || 0}</p>
                <p className="text-xs" style={{ color: '#8A8798' }}>longest streak</p>
              </div>
              <div>
                <p className="text-lg" style={{ color: '#2E2E42', fontFamily: 'Playfair Display, serif' }}>{journey?.grace_days_used || 0}</p>
                <p className="text-xs" style={{ color: '#8A8798' }}>grace days used</p>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Milestones */}
        {journey?.milestones?.length > 0 && (
          <AnimatedSection delay="0.4s" animateIn={animateIn}>
            <SectionHeader>MILESTONES</SectionHeader>
            <div className="relative pl-8">
              <div className="absolute left-3 top-0 bottom-0 w-0.5" style={{ backgroundColor: 'rgba(201,168,76,0.2)' }} />
              {journey.milestones.map((milestone, idx) => (
                <div key={`milestone-${idx}`} className="relative mb-6 last:mb-0">
                  <div className="absolute -left-5 top-1 w-3 h-3 rounded-full" style={{ backgroundColor: '#C9A84C', boxShadow: '0 0 10px rgba(201,168,76,0.5)' }} />
                  <p className="text-xs mb-1" style={{ color: '#A9756B' }}>{milestone.date || 'Recent'}</p>
                  <p className="text-sm" style={{ fontFamily: 'Playfair Display, serif', color: '#2E2E42' }}>
                    {typeof milestone === 'string' ? milestone : milestone.text || milestone.description}
                  </p>
                </div>
              ))}
            </div>
          </AnimatedSection>
        )}

        {/* Phase Progress */}
        <AnimatedSection delay="0.7s" animateIn={animateIn}>
          <SectionHeader>YOUR HEALING PATH</SectionHeader>
          <div className="flex items-center justify-between relative">
            <div className="absolute top-1/2 left-0 right-0 h-0.5 -translate-y-1/2" style={{ backgroundColor: '#EAE4DC' }} />
            <div className="absolute top-1/2 left-0 h-0.5 -translate-y-1/2 transition-all duration-1000" style={{ backgroundColor: '#C9A84C', width: `${((journey?.current_phase || 1) / 4) * 100}%` }} />
            {PHASES.map((phase) => {
              const isCurrent = phase.id === journey?.current_phase;
              const isCompleted = phase.id < (journey?.current_phase || 1);
              return (
                <div key={phase.id} className="relative flex flex-col items-center z-10">
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-500 ${isCurrent ? 'scale-125' : ''}`}
                    style={{ 
                      backgroundColor: isCurrent || isCompleted ? '#C9A84C' : '#EAE4DC',
                      color: isCurrent || isCompleted ? '#FBF9F6' : '#8A8798',
                      boxShadow: isCurrent ? '0 0 20px rgba(201,168,76,0.5)' : 'none'
                    }}
                  >
                    {phase.id}
                  </div>
                  <p className="text-xs mt-2 whitespace-nowrap" style={{ color: isCurrent ? '#C9A84C' : '#8A8798' }}>{phase.name}</p>
                </div>
              );
            })}
          </div>
        </AnimatedSection>

        {/* Phase Journey Timeline (dated) */}
        <AnimatedSection delay="0.75s" animateIn={animateIn}>
          <SectionHeader>YOUR PHASE JOURNEY</SectionHeader>
          <div className="p-6 rounded-2xl" style={{ background: '#F9F4F0' }}>
            <PhaseJourneyTimeline variant="dark" />
          </div>
        </AnimatedSection>

        {/* Workbooks */}
        <AnimatedSection delay="0.8s" animateIn={animateIn}>
          <SectionHeader>PHASE WORKBOOKS</SectionHeader>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(PHASE_WORKBOOKS).map(([phaseId, workbook]) => {
              const phase = parseInt(phaseId);
              const isCurrent = phase === journey?.current_phase;
              const isUnlocked = phase <= (journey?.current_phase || 1);
              
              return (
                <a
                  key={phaseId}
                  href={isUnlocked ? workbook.url : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-4 rounded-xl transition-all duration-300 ${isUnlocked ? 'hover:scale-[1.02] cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                  style={{ 
                    background: isCurrent ? 'rgba(201,168,76,0.12)' : '#F9F4F0',
                    border: isCurrent ? '1px solid rgba(201,168,76,0.3)' : '1px solid #F7F1EE'
                  }}
                  onClick={(e) => { if (!isUnlocked) { e.preventDefault(); toast.info('Complete earlier phases to unlock'); }}}
                  data-testid={`workbook-phase-${phaseId}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isUnlocked ? 'rgba(201,168,76,0.2)' : '#F7F1EE' }}>
                      <Download className="w-5 h-5" style={{ color: isUnlocked ? '#C9A84C' : '#A3A0AE' }} />
                    </div>
                    <div>
                      <p className="font-medium text-sm" style={{ color: isUnlocked ? '#1A1A2E' : '#8A8798', fontFamily: 'Jost, sans-serif' }}>
                        Phase {phase}: {workbook.name}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: '#8A8798' }}>{workbook.description}</p>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </AnimatedSection>
      </main>

      <CrisisFooter />
    </div>
  );
};

export default ProgressPage;
